import React, { useRef, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { useHistory } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import {
  Avatar,
  Box,
  ButtonBase,
  Divider,
  Paper,
  Popover,
  Typography,
  makeStyles,
} from '@material-ui/core'
import {
  User as UserProfileIcon,
  LogOut as LogOutIcon,
  ChevronDown as ChevronDownIcon,
} from 'react-feather'
import { SvgIcon } from '@material-ui/core'
import useAuth from 'src/hooks/useAuth'

const useStyles = makeStyles(() => ({
  trigger: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '4px 8px',
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'background 0.15s',
    '&:hover': { background: 'rgba(255,255,255,0.08)' },
  },
  avatar: {
    width: 32,
    height: 32,
    fontSize: 13,
    fontWeight: 700,
    background: '#7c3aed',
    flexShrink: 0,
  },
  nameBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    lineHeight: 1.2,
  },
  userName: {
    fontSize: 13,
    fontWeight: 700,
    color: '#fff',
    whiteSpace: 'nowrap',
  },
  userSub: {
    fontSize: 11,
    color: 'rgba(255,255,255,0.6)',
    whiteSpace: 'nowrap',
    maxWidth: 130,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  paper: {
    width: 180,
    borderRadius: 12,
    overflow: 'hidden',
    boxShadow: '0 8px 32px rgba(0,0,0,0.16)',
    marginTop: 8,
  },
  menuHeader: {
    padding: '16px 20px',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    background: '#1a1a2e',
  },
  headerAvatar: {
    width: 40,
    height: 40,
    fontSize: 15,
    fontWeight: 700,
    background: '#7c3aed',
    flexShrink: 0,
  },
  headerName: {
    fontWeight: 800,
    fontSize: 14,
    color: '#fff',
  },
  headerSub: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.6)',
    marginTop: 1,
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '13px 20px',
    cursor: 'pointer',
    fontSize: 14,
    color: '#1a1a2e',
    fontWeight: 500,
    transition: 'background 0.12s',
    '&:hover': { background: '#f3f4f6' },
  },
  menuIcon: {
    width: 36,
    height: 36,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f3f4f6',
    flexShrink: 0,
  },
  logoutIcon: {
    background: '#fef2f2',
  },
  logoutText: {
    color: '#dc2626',
  },
}))

const Account = () => {
  const classes = useStyles()
  const history = useHistory()
  const ref = useRef(null)
  const { account, logout } = useAuth()
  const { enqueueSnackbar } = useSnackbar()
  const [isOpen, setOpen] = useState(false)

  const initials = (account.name || 'U').trim().split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase()

  const handleLogout = async () => {
    try {
      setOpen(false)
      await logout()
      history.push('/')
    } catch (err) {
      console.error(err)
      enqueueSnackbar('Unable to logout', { variant: 'error' })
    }
  }

  return (
    <>
      <Box
        component={ButtonBase}
        className={classes.trigger}
        onClick={() => setOpen(true)}
        ref={ref}
      >
        <Avatar className={classes.avatar}>{initials}</Avatar>
        <Box className={classes.nameBlock}>
          <Typography className={classes.userName}>{account.name}</Typography>
          {account.roleName && (
            <Typography className={classes.userSub}>{account.roleName}</Typography>
          )}
        </Box>
        <SvgIcon style={{ fontSize: 14, color: 'rgba(255,255,255,0.6)', marginLeft: 2 }}>
          <ChevronDownIcon />
        </SvgIcon>
      </Box>

      <Popover
        open={isOpen}
        anchorEl={ref.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ className: classes.paper }}
      >
        {/* User Profile */}
        <Box className={classes.menuItem} onClick={() => { setOpen(false); history.push('/app/accounts') }}>
          <Box className={classes.menuIcon}>
            <SvgIcon style={{ fontSize: 18, color: '#555' }}><UserProfileIcon /></SvgIcon>
          </Box>
          User Profile
        </Box>

        <Divider />

        {/* Log Out */}
        <Box className={`${classes.menuItem} ${classes.logoutText}`} onClick={handleLogout}>
          <Box className={`${classes.menuIcon} ${classes.logoutIcon}`}>
            <SvgIcon style={{ fontSize: 18, color: '#dc2626' }}><LogOutIcon /></SvgIcon>
          </Box>
          Log Out
        </Box>
      </Popover>
    </>
  )
}

export default Account
